/**
 * API utility functions for making authenticated requests to the remote backend
 * 
 * This file provides comprehensive API utilities with proper error handling,
 * retry logic, and logging for the NovaQ Finance System.
 */

import { API_CONFIG, API_ENDPOINTS } from '../../config/api';
import { getLegacyToken } from '../../config/etaxToken';
import { 
  ErrorFactory, 
  errorHandler, 
  errorLogger, 
  formatErrorForUser,
  isRetryableError,
  createErrorContext 
} from './errorHandler';
import { 
  AppError, 
  ApiError, 
  ErrorCodes, 
  ApiResponse,
  ErrorResponse,
  SuccessResponse 
} from '../types/errors';

const API_BASE_URL = API_CONFIG.BASE_URL;

// Get authentication headers
const getAuthHeaders = () => {
  // Production environment-д localStorage-аас token авахгүй
  let novaqToken = null;
  
  if (process.env.NODE_ENV === 'development') {
    novaqToken = localStorage.getItem('novaqToken');
    console.log('🔑 Development: novaqToken from localStorage:', novaqToken ? `${novaqToken.substring(0, 20)}...` : 'NO TOKEN');
  } else {
    // Production-д sessionStorage ашиглах эсвэл secure storage
    novaqToken = sessionStorage.getItem('novaqToken') || localStorage.getItem('novaqToken');
    console.log('🔑 Production: novaqToken from secure storage:', novaqToken ? `${novaqToken.substring(0, 20)}...` : 'NO TOKEN');
  }
  
  console.log('🔑 Full novaqToken length:', novaqToken ? novaqToken.length : 0);
  console.log('🔑 AccountantOid from storage:', localStorage.getItem('accountantOid'));
  
  if (!novaqToken) {
    console.error('❌ No token found - user needs to login again');
    return {
      'Content-Type': 'application/json'
    };
  }
  
  // Token format шалгах
  if (novaqToken.length < 10) {
    console.error('❌ novaqToken too short, likely invalid:', novaqToken);
    return {
      'Content-Type': 'application/json'
    };
  }
  
  return {
    'Authorization': `Bearer ${novaqToken}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Generic API call function with retry logic and comprehensive error handling
 */
export const apiCall = async <T = any>(
  endpoint: string, 
  options: RequestInit = {},
  retryCount: number = 0
): Promise<T> => {
  const url = `${API_BASE_URL.trim()}${endpoint}`;
  const method = options.method || 'GET';
  const headers = {
    ...getAuthHeaders(),
    ...options.headers
  };

  const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const errorContext = createErrorContext({ requestId, endpoint, method });

  errorLogger.logInfo(`API Call started: ${method} ${endpoint}`, {
    requestId,
    url,
    headers: { ...headers, Authorization: headers.Authorization ? '[REDACTED]' : undefined },
    body: options.body ? 'Present' : 'None'
  });
  
  // Debug: Log detailed headers (filter out routine NE-KEY API calls)
  if (!endpoint.includes('/api/tax-ne-key')) {
    console.log('🔍 API Call Headers Debug:');
    console.log('  - Authorization:', headers.Authorization ? `${headers.Authorization.substring(0, 20)}...` : 'None');
    console.log('  - Content-Type:', headers['Content-Type'] || 'None');
    console.log('  - X-Requested-With:', headers['X-Requested-With'] || 'None');
    console.log('  - All headers keys:', Object.keys(headers));
    console.log('  - Full URL:', url);
    console.log('  - Method:', method);
    console.log('  - Environment:', process.env.NODE_ENV);
  }

  // Special handling for save-all endpoint - check token validity first
  if (endpoint === '/api/customer/save-all') {
    await validateToken();
  }

  try {
    const controller = new AbortController();
    // Special timeout for ledger rebuild endpoint (can take up to 6 minutes)
    const timeoutDuration = endpoint.includes('/api/cron/ledger/rebuild/customer') 
      ? 400000 // 400 seconds (6.67 minutes) for ledger rebuild
      : API_CONFIG.TIMEOUT;
    const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);

    const response = await fetch(url, {
      ...options,
      headers,
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    errorLogger.logInfo(`API Response received: ${response.status} ${response.statusText}`, {
      requestId,
      endpoint,
      status: response.status,
      statusText: response.statusText,
      contentType: response.headers.get('content-type')
    });
    
    // Debug: Log response details (filter out routine NE-KEY API calls)
    if (!endpoint.includes('/api/tax-ne-key')) {
      console.log('🔍 API Response Debug:');
      console.log('  - Status:', response.status);
      console.log('  - Status Text:', response.statusText);
      console.log('  - Content-Type:', response.headers.get('content-type'));
      console.log('  - URL:', response.url);
      console.log('  - OK:', response.ok);
    }

    const contentType = response.headers.get('content-type');
    
    if (!response.ok) {
      const apiError = await handleErrorResponse(response, endpoint, method, requestId);
      
      // Retry logic for retryable errors (but not for routine endpoints)
      const isRoutineEndpoint = endpoint.includes('/stats');
      if (isRetryableError(apiError) && retryCount < API_CONFIG.RETRY_ATTEMPTS && !isRoutineEndpoint) {
        errorLogger.logWarning(`Retrying API call (attempt ${retryCount + 1}/${API_CONFIG.RETRY_ATTEMPTS})`, {
          requestId,
          error: apiError.message,
          retryCount: retryCount + 1
        });
        
        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
        return apiCall<T>(endpoint, options, retryCount + 1);
      }
      
      errorHandler.handle(apiError);
      throw apiError;
    }

    // Parse successful response
    const data = await parseResponse(response, contentType, requestId);
    
    errorLogger.logInfo(`API Call completed successfully`, {
      requestId,
      endpoint,
      method,
      dataSize: JSON.stringify(data).length
    });

    return data;
  } catch (error) {
    const appError = handleFetchError(error, endpoint, method, requestId);
    
    // Retry logic for network errors
    if (isRetryableError(appError) && retryCount < API_CONFIG.RETRY_ATTEMPTS) {
      errorLogger.logWarning(`Retrying API call due to network error (attempt ${retryCount + 1}/${API_CONFIG.RETRY_ATTEMPTS})`, {
        requestId,
        error: appError.message,
        retryCount: retryCount + 1
      });
      
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
      return apiCall<T>(endpoint, options, retryCount + 1);
    }
    
    errorHandler.handle(appError);
    throw appError;
  }
};

/**
 * Validate token before making critical API calls
 */
const validateToken = async (): Promise<void> => {
  try {
    const token = getLegacyToken();
    if (!token) {
      throw ErrorFactory.createAuthError('token_invalid', 'No token found');
    }

    // Simple token validation - just check if token exists
    if (!token || token.length === 0) {
      throw ErrorFactory.createAuthError('token_invalid', 'No valid token found');
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Handle error responses from API
 */
const handleErrorResponse = async (
  response: Response, 
  endpoint: string, 
  method: string,
  requestId: string
): Promise<ApiError> => {
  const contentType = response.headers.get('content-type');
  let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
  let responseData: any = null;

  try {
    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
      errorMessage = responseData.message || responseData.error || errorMessage;
    } else {
      const errorText = await response.text();
      errorMessage = `Server returned non-JSON response: ${errorText.substring(0, 200)}`;
    }
  } catch (parseError) {
    errorLogger.logWarning('Failed to parse error response', {
      requestId,
      status: response.status,
      parseError: parseError instanceof Error ? parseError.message : 'Unknown'
    });
  }

  return ErrorFactory.createApiError(
    response.status,
    response.statusText,
    endpoint,
    method,
    errorMessage,
    responseData,
    { requestId }
  );
};

/**
 * Parse successful response
 */
const parseResponse = async (
  response: Response, 
  contentType: string | null, 
  requestId: string
): Promise<any> => {
  if (contentType && contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch (parseError) {
      errorLogger.logWarning('Failed to parse JSON response', {
        requestId,
        parseError: parseError instanceof Error ? parseError.message : 'Unknown'
      });
      throw ErrorFactory.createBusinessError(
        'system',
        'parse_response',
        'Failed to parse server response',
        'high',
        { requestId }
      );
    }
  } else {
    const textResponse = await response.text();
    errorLogger.logWarning('Server sent non-JSON response', {
      requestId,
      contentType,
      responseLength: textResponse.length,
      responsePreview: textResponse.substring(0, 200)
    });
    
    // Production-д text/html response ирвэл алдаа throw хийх
    if (process.env.NODE_ENV === 'production' && contentType && contentType.includes('text/html')) {
      console.error('❌ Production: Server returned HTML instead of JSON');
      console.error('❌ Response preview:', textResponse.substring(0, 500));
      throw ErrorFactory.createBusinessError(
        'system',
        'invalid_response_format',
        'Server returned HTML instead of JSON response',
        'high',
        { requestId, contentType, responsePreview: textResponse.substring(0, 200) }
      );
    }
    
    throw ErrorFactory.createBusinessError(
      'system',
      'parse_response',
      'Server sent non-JSON response',
      'medium',
      { requestId, contentType }
    );
  }
};

/**
 * Handle fetch errors (network, timeout, etc.)
 */
const handleFetchError = (
  error: unknown, 
  endpoint: string, 
  method: string,
  requestId: string
): AppError => {
  if (error instanceof Error) {
    if (error.name === 'AbortError') {
      return ErrorFactory.createNetworkError(
        'timeout',
        `${API_BASE_URL}${endpoint}`,
        'Request timeout',
        true,
        { requestId }
      );
    }
    
    if (error.message.includes('fetch')) {
      return ErrorFactory.createNetworkError(
        'connection_failed',
        `${API_BASE_URL}${endpoint}`,
        'Network connection failed',
        true,
        { requestId, originalError: error.message }
      );
    }
    
    return ErrorFactory.createBusinessError(
      'system',
      'api_call',
      error.message,
      'medium',
      { requestId, originalError: error.message }
    );
  }
  
  return ErrorFactory.createBusinessError(
    'system',
    'api_call',
    'Unknown error occurred',
    'high',
    { requestId, error: String(error) }
  );
};

// Generic API methods for backward compatibility
const api = {
  // Generic methods
  get: (endpoint: string) => apiCall(endpoint),
  post: (endpoint: string, data: any) => apiCall(endpoint, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  put: (endpoint: string, data: any) => apiCall(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  delete: (endpoint: string) => apiCall(endpoint, {
    method: 'DELETE'
  }),

  // Specific API functions
  // Accountants
  getAccountants: () => apiCall(API_ENDPOINTS.ACCOUNTANTS.BASE),
  createAccountant: (data: any) => apiCall(API_ENDPOINTS.ACCOUNTANTS.BASE, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  updateAccountant: (id: string, data: any) => apiCall(`/api/accountant/accountants/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  checkAccountantRegister: (registerNo: string) => apiCall(`/api/accountant/accountants/check-register/${registerNo}`),

  // Customers
  getCustomers: () => apiCall(API_ENDPOINTS.CUSTOMERS.BASE),
  getBusinessClasses: () => apiCall(API_ENDPOINTS.CUSTOMERS.BUSINESS_CLASSES),
  getRegions: () => apiCall(API_ENDPOINTS.CUSTOMERS.REGIONS),
  getRegionSubs: (regionId: string) => apiCall(`/api/regions/${regionId}/subs`),
  updateCustomer: (customerId: string, data: any) => apiCall(`/update-customer/${customerId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  getCustomerWithEmployees: (customerOid: string) => apiCall(`/api/customer/customer-with-employees?CustomerOid=${customerOid}`),
  updateCustomerFromEmployees: (customerOid: string, data: { customer: any; employees: any[] }) => apiCall(`/api/customer/update-from-employees?CustomerOid=${customerOid}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  deleteCustomer: (customerOid: string) => apiCall(`/deletecustomer/${customerOid}`, {
    method: 'DELETE'
  }),

  // Roles
  getRoles: () => apiCall(API_ENDPOINTS.USERS.ROLES),

  // Users
  getUsers: () => apiCall(API_ENDPOINTS.USERS.BASE),
  createUser: (data: any) => apiCall(API_ENDPOINTS.USERS.BASE, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  updateUser: (id: string, data: any) => apiCall(`/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  deleteUser: (id: string) => apiCall(`/users/${id}`, {
    method: 'DELETE'
  }),
  checkUserRegister: (registerNo: string) => apiCall(`${API_ENDPOINTS.USERS.CHECK_REGISTER}/${registerNo}`),
  getCustomersForDropdown: () => apiCall('/customers-for-dropdown'),
  getCustomerByPin: (pin: string) => apiCall(`/api/customerslist/customer-by-pin/${pin}`),
  getAccountantsForDropdown: () => apiCall('/api/accountant/accountants-for-dropdown'),

  // Tax and Insurance
  getTaxLogins: (accountantOid?: string) => {
    const qs = accountantOid
      ? `?AccountantOid=${encodeURIComponent(accountantOid)}`
      : '';
    return apiCall(`/api/tax-login${qs}`);
  },
  getTaxLoginByOid: (taxLoginOid: string) =>
    apiCall(`/api/tax-login?Oid=${encodeURIComponent(taxLoginOid)}`),
  getTaxNeKey: () => apiCall('/tax-ne-key'),
  getEtaxUserOrgs: (data: any) => apiCall('/etax-proxy/getUserOrgs', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  createTaxLogin: (data: any) => apiCall('/api/tax-login', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  updateTaxLogin: (id: string, data: any) =>
    apiCall(`/api/tax-login?Oid=${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
  fetchCustomerData: (data: any) => apiCall('/api/fetch-customer-data', {
    method: 'POST',
    body: JSON.stringify(data)
  }),

  // Column Settings
  getColumnSettings: async (settingsKey: string) => {
    try {
      console.log('🔍 Column settings авах оролдлого:', settingsKey);
      
      // Production орчинд column settings API-г шалгах
      if (process.env.NODE_ENV === 'production') {
        console.log('🔍 Production орчинд column settings API дуудаж байна...');
        
        // API дуудалт хийх
        const result = await apiCall(`/api/column-settings/${settingsKey}`);
        console.log('✅ Column settings амжилттай авлаа:', result);
        return result;
      } else {
        // Development орчинд mock data буцаах
        console.log('🔍 Development орчинд mock column settings буцааж байна...');
        return {
          success: true,
          data: {
            columnOrder: null,
            columnVisibility: null,
            columnWidths: null
          }
        };
      }
    } catch (error) {
      console.warn('⚠️ Column settings авахад алдаа:', error);
      // Production болон development-д column settings авахгүй бол default утга буцаах
      console.warn('⚠️ Column settings not available, using defaults:', settingsKey);
      return {
        success: true,
        data: {
          columnOrder: null,
          columnVisibility: null,
          columnWidths: null
        }
      };
    }
  },
  saveColumnSettings: (settingsKey: string, data: any) => {
    console.log('💾 Column settings хадгалах оролдлого:', settingsKey, data);
    return apiCall(`/api/column-settings/${settingsKey}`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },
  deleteTaxLogin: (id: string) => apiCall(`/api/tax-login?Oid=${encodeURIComponent(id)}`, {
    method: 'DELETE'
  }),

  getInsuranceLogins: (accountantOid: string) => apiCall(`/api/insurance-login/${accountantOid}`),
  createInsuranceLogin: (data: any) => apiCall('/api/insurance-login', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  updateInsuranceLogin: (id: string, data: any) => apiCall(`/api/insurance-login/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  deleteInsuranceLogin: (id: string) => apiCall(`/api/insurance-login/${id}`, {
    method: 'DELETE'
  }),

  // Customer credentials
  getUserCredentials: (customerId: string) => apiCall(`/api/user-credentials/${customerId}`),

  // Customer employees (syncEmpId=true → эхлээд empId sync, дараа нь ажилчдыг буцаана)
  getCustomerEmployees: (customerId: string, syncEmpId = true) =>
    apiCall(`/api/customer-employees/${customerId}${syncEmpId ? '?syncEmpId=true' : ''}`),
  syncCustomerEmployees: (customerId: string, insuranceLoginId: string, insuranceLoginPassword: string, customerCode?: string, customerName?: string, isNewCustomer?: boolean) => apiCall(`/api/sync-employees/${customerId}`, {
    method: 'POST',
    body: JSON.stringify({ 
      insuranceLoginId, 
      insuranceLoginPassword, 
      customerCode, 
      customerName, 
      isNewCustomer 
    })
  }),
  


  // Save customer
  saveCustomer: (data: any) => apiCall(API_ENDPOINTS.CUSTOMERS.SAVE_ALL, {
    method: 'POST',
    body: JSON.stringify(data)
  }),

  // File upload
  uploadCert: (formData: FormData) => {
    const token = getLegacyToken();
    return fetch(`${API_BASE_URL}/upload-cert`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
  },

  // Save all (customer + user + accountant)
  saveAll: (data: any) => apiCall(API_ENDPOINTS.CUSTOMERS.SAVE_ALL, {
    method: 'POST',
    body: JSON.stringify(data)
  }),


  // Statistics
  getAccountantStats: async () => {
    try {
      console.log('📊 Fetching real stats from API');
      console.log('📊 API Base URL:', API_BASE_URL);
      console.log('📊 Full endpoint:', `${API_BASE_URL}/api/customerslist/accountant-stats`);
      
      const response = await apiCall(API_ENDPOINTS.ACCOUNTANTS.STATS);
      console.log('📊 Raw API response:', response);
      return response;
    } catch (error) {
      console.warn('⚠️ Stats API failed, using fallback data:', error);
      console.warn('⚠️ Error details:', {
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        url: error.url
      });
      
      // API амжилтгүй бол fallback data буцаах
      return {
        success: true,
        data: {
          totalCustomers: 0,
          activeCustomers: 0,
          totalReports: 0,
          totalUsers: 0
        }
      };
    }
  },
  getAdminStats: () => {
    console.log('📊 getAdminStats: Returning mock data instead of API call');
    return Promise.resolve({
      success: true,
      data: {
        totalUsers: 25,
        totalAccountants: 8,
        totalCustomers: 120,
        totalReports: 350
      }
    });
  },

  // Login History for different roles
  saveLoginHistory: (data: any, role: string = 'accountant') => {
    const endpoint = `/auth/${role.toLowerCase()}`;
    const fullUrl = `${API_BASE_URL}${endpoint}`;
    
    console.log('🔍 saveLoginHistory called with:', {
      endpoint,
      fullUrl,
      role,
      data,
      dataStringified: JSON.stringify(data),
      headers: getAuthHeaders()
    });
    
    return apiCall(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    }).catch(error => {
      console.error('❌ saveLoginHistory failed:', {
        endpoint,
        fullUrl,
        error: error.message,
        data
      });
      throw error;
    });
  },

  // Specific login history methods for each role
  saveAccountantLoginHistory: (data: any) => apiCall('api/auth/accountant', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  saveAdminLoginHistory: (data: any) => apiCall('api/auth/admin', {
    method: 'POST',
    body: JSON.stringify(data)
  })
};

export { API_BASE_URL, getAuthHeaders };
export default api; 